import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import { ResponsiveFunnel, type FunnelDatum } from '@nivo/funnel';
import type { ComponentProps } from 'react';

import { useWidgetContext } from 'components/Dashboard/Widget';
import { CHART_HEIGHT } from 'components/Dashboard/shared/constants';
import { formatFunnelValue, getFunnelChartTheme } from './utils';
import { useFunnelData } from './useFunnelData';
import WidgetError from 'components/Dashboard/components/WidgetError';

// Extract the tooltip prop type from ResponsiveFunnel
type FunnelTooltipProp = NonNullable<
  ComponentProps<typeof ResponsiveFunnel<FunnelDatum>>['tooltip']
>;
type FunnelPartTooltipProps = Parameters<FunnelTooltipProp>[0];

// Custom tooltip component to show percentage difference between funnel steps
const FunnelTooltip = ({
  part,
  data,
}: {
  part: FunnelPartTooltipProps;
  data: FunnelDatum[];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const _part = part?.part;
  const currentIndex = data.findIndex((item) => item.id === _part?.data?.id);
  const previousValue = currentIndex > 0 ? data[currentIndex - 1].value : null;
  const currentValue = _part?.data?.value ?? 0;

  // Calculate percentage difference from previous step
  const percentageDiff =
    previousValue !== null
      ? ((currentValue - previousValue) / previousValue) * 100
      : null;

  const formattedValue = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(currentValue);

  const id = _part?.data?.id || 'tooltip-' + v4();
  const label = _part?.data?.label || '';

  // Theme-aware colors
  const isLightMode = theme.palette.mode === 'light';
  const backgroundColor = isLightMode
    ? (theme.blox?.colors?.offwhite ?? '#FFFFFF')
    : (theme.blox?.colors?.darkgray ?? '#2D2D2D');
  const textColor = isLightMode
    ? (theme.blox?.colors?.offblack ?? '#000000')
    : (theme.blox?.colors?.offwhite ?? '#FFFFFF');

  return (
    <Box
      data-testid={`funnel-chart-tooltip--${id}`}
      sx={{
        background: backgroundColor,
        padding: '9px 12px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 9999,
        position: 'relative',
        minWidth: 220,
      }}
    >
      <Typography
        data-testid={`funnel-chart-tooltip-label--${id}`}
        variant="body2"
        sx={{
          fontWeight: 600,
          color: textColor,
        }}
      >
        {label}
      </Typography>
      <Typography
        data-testid={`funnel-chart-tooltip-value--${id}`}
        variant="h4"
        sx={{ mt: 0.5, color: textColor, fontWeight: 700 }}
      >
        {formattedValue}
      </Typography>
      {percentageDiff !== null && (
        <Typography
          data-testid={`funnel-chart-tooltip-diff--${id}`}
          variant="body2"
          sx={{
            mt: 0.5,
            color: percentageDiff >= 0 ? 'success.main' : 'error.main',
          }}
        >
          {t('blox.dashboards.charts.funnel.percent_diff', {
            percent: Math.abs(percentageDiff).toFixed(1),
            direction: percentageDiff >= 0 ? 'up' : 'down',
          })}
        </Typography>
      )}
    </Box>
  );
};

// Funnel Chart that uses the Nivo library - https://nivo.rocks/funnel/.
const FunnelChart = () => {
  const { widget, data, config } = useWidgetContext();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const funnelTheme = getFunnelChartTheme(theme);
  const greenSpectrum = Object.values(green).slice(3, 12);

  // const isHorizontal = true;
  const { parsedData, hasData, isNailShape, isHorizontal, showPercentDiff } =
    useFunnelData({
      widget,
      data,
      config,
    });

  return (
    <Box
      data-testid="funnel-chart-container"
      sx={{
        height: !hasData ? 'auto' : CHART_HEIGHT + 36,
        minHeight: CHART_HEIGHT,
        width: '100%',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {!hasData && <WidgetError type="noData" />}
      {hasData && (
        <Stack
          data-testid={`funnel-chart-wrapper--${widget?.id}`}
          direction={{ xs: 'column', sm: isHorizontal ? 'column' : 'row' }}
          sx={{
            height: CHART_HEIGHT,
            justifyContent: isHorizontal ? 'center' : 'flex-start',
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {/* Labels */}
          {(!isFullScreen || isNailShape) && !isHorizontal && (
            <Stack
              data-testid={`funnel-chart-labels--${widget?.id}`}
              sx={{
                justifyContent: 'space-around',
                alignItems: { xs: 'center', sm: 'flex-end' },
                width: { xs: '100%', sm: '20%' },
                textAlign: { xs: 'center', sm: 'right' },
                overflow: 'hidden',
              }}
            >
              {parsedData.map((data: FunnelDatum) => (
                <Typography
                  key={data.id}
                  variant="body1"
                  component="div"
                  sx={[
                    {
                      fontWeight: 600,
                    },
                    (theme) => ({
                      fontSize: '16px',
                      color:
                        theme.palette.mode === 'light'
                          ? theme.blox?.colors?.bluegray
                          : theme.blox?.colors?.white,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }),
                  ]}
                >
                  {formatFunnelValue(data.label!, data.value, isNailShape)}
                </Typography>
              ))}
            </Stack>
          )}

          {/* Chart. */}
          <Box
            data-testid={`funnel-chart-wrapper--${widget?.id}--outer`}
            sx={{
              width: isFullScreen || isHorizontal ? '100%' : '80%',
              height: isHorizontal ? CHART_HEIGHT / 2 : CHART_HEIGHT,
              overflow: 'hidden',
              position: 'relative',
              zIndex: 0,
            }}
          >
            <Box
              data-testid={`funnel-chart--${widget?.id}`}
              sx={{
                width: '100%',
                height: CHART_HEIGHT,
                position: 'relative',
                overflow: 'visible',
                svg: {
                  overflow: 'visible',
                },
                // Ensure tooltip renders above other elements
                '& > div': {
                  zIndex: 9999,
                },
              }}
            >
              <ResponsiveFunnel
                data={parsedData}
                valueFormat=",.0f"
                theme={funnelTheme}
                colors={greenSpectrum}
                borderWidth={20}
                labelColor={{ theme: 'text.fill' }}
                beforeSeparatorLength={10}
                beforeSeparatorOffset={10}
                afterSeparatorLength={10}
                afterSeparatorOffset={10}
                currentPartSizeExtension={10}
                currentBorderWidth={40}
                shapeBlending={0.2}
                enableLabel={!isNailShape}
                direction={isHorizontal ? 'horizontal' : 'vertical'}
                margin={{
                  bottom: 1,
                  top: 1,
                }}
                tooltip={
                  !showPercentDiff
                    ? undefined
                    : (part) => (
                        <FunnelTooltip
                          part={part}
                          data={parsedData}
                        />
                      )
                }
              />
            </Box>
          </Box>

          {isHorizontal && (
            <Stack
              data-testid={`funnel-chart-labels-horizontal--${widget?.id}`}
              direction="row"
              sx={{
                justifyContent: 'space-around',
                alignItems: 'flex-start',
                width: '100%',
                mt: 1,

                display: {
                  xs: 'none',
                  sm: 'flex',
                },
              }}
            >
              {parsedData.map((data: FunnelDatum) => (
                <Typography
                  data-testid={`funnel-chart-label--${widget?.id}--${data.id}`}
                  key={data.id}
                  variant="body1"
                  component="div"
                  sx={[
                    {
                      fontWeight: 600,
                      textAlign: 'center',
                    },
                    (theme) => ({
                      fontSize: '14px',
                      color:
                        theme.palette.mode === 'light'
                          ? theme.blox?.colors?.bluegray
                          : theme.blox?.colors?.white,
                      flex: 1,
                      wordBreak: 'break-word',
                    }),
                  ]}
                >
                  {formatFunnelValue(data.label!, data.value, isNailShape, false)}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default FunnelChart;
